// A solution's panel (02-DESIGN/14-solutions/03-the-console.md § where a
// solution appears, § the declaration): `/<slug>/solutions/<name>/<panel>`,
// drawn from the installed manifest's declaration with the shared anatomy —
// the title, one line, then the elements — and `/<slug>/solutions/<name>/page`,
// the solution's own page in a frame. The elements a role's template would
// refuse are not drawn for it, nor those whose role has no workload placed
// here; a panel left with nothing drawn says so in one line (hq issue 151).
// A declared list's row opens the record's page,
// `/<slug>/solutions/<name>/<panel>/<key>`, with a crumb back to the panel,
// and a declared form shows in the panel's place (14-solutions/03 § the
// elements; hq decision 0069).

import { Link, Navigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { Empty, NotAllowed, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { SERVICE, Subjects, type Record_ } from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import { useFormState } from "../../ui/form.tsx";
import { DeclaredForm, Elements, RecordPage } from "./Elements.tsx";
import { FramePage } from "./Frame.tsx";
import { OBSERVABILITY } from "./installed.ts";
import { drawnFor, forAudience, openForm, panelSent, recordList } from "./panel.ts";
import { panelLine } from "./PanelLine.tsx";
import { PAGE_TOKEN } from "./pages.ts";
import { useGrantsRead } from "./sends.ts";
import { Chips, usePanelSelection } from "./Selection.tsx";
import { Audience } from "../../wire/solutions.ts";

/** PAGE is the address token of a solution's own page beside its panels. */
export const PAGE = PAGE_TOKEN;

export function PanelPage() {
  const { name = "", panel = "" } = useParams();
  return <Panel name={name} panel={panel} />;
}

/** PanelRecordPage is a declared list's row as its record's page: `solutions/<name>/<panel>/<key>`. */
export function PanelRecordPage() {
  const { name = "", panel = "", key = "" } = useParams();
  return <Panel name={name} panel={panel} recordKey={key} />;
}

function Panel({ name, panel, recordKey }: { name: string; panel: string; recordKey?: string }) {
  const s = useSurface();
  const forms = useFormState();
  const [load] = useLoad(`solution/panel/${name}`, () =>
    s.bus.call<Record_>(SERVICE, Subjects.installedGet(name), undefined),
  );
  const declared =
    load.state === "pending" || load.state === "refused"
      ? undefined
      : load.value.manifest?.panels?.find((x) => x.name === panel);
  const { selection, select, clear } = usePanelSelection(declared);
  const admin = s.offered("solutions:manage");
  const { grants, ready: grantsRead } = useGrantsRead(s.bus, admin);
  // Whether a panel exists here, and which of its lists holds records, is
  // decided once the grants have answered, never on the empty set before.
  if (load.state === "pending" || !grantsRead) {
    return <Pending what="Reading the solution" />;
  }
  const crumb = (title: string) => (
    <>
      <Link to={tenantPath(s.tenant, "solutions")}>Solutions</Link>
      <span>/</span>
      <Link to={tenantPath(s.tenant, "solutions", name)}>{title}</Link>
    </>
  );
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb(name)} title={name} />
        <NotAllowed err={load.err} what="This solution" />
      </>
    );
  }
  const r = load.value;
  const m = r.manifest;
  if (m === undefined) {
    return <PageHead crumb={crumb(name)} title={name} line="Not installed here." />;
  }
  if (panel === PAGE && m.page !== undefined) {
    return <FramePage r={r} m={m} page={m.page} crumb={crumb(m.display)} />;
  }
  const p = m.panels?.find((x) => x.name === panel);
  if (p !== undefined && name === OBSERVABILITY && p.audience !== Audience.person) {
    // Its admin panels are the tabs of Audit & usage, not pages under
    // Solutions (hq decision 0048); its person's panel, Your usage, is here.
    return (
      <Navigate
        to={
          recordKey === undefined
            ? tenantPath(s.tenant, "audit")
            : tenantPath(s.tenant, "audit", panel, recordKey)
        }
        replace
      />
    );
  }
  if (p === undefined) {
    return (
      <>
        <PageHead crumb={crumb(m.display)} title="Not a panel" />
        <Empty>{m.display} declares no panel of that name.</Empty>
      </>
    );
  }
  const ids = r.workloads ?? {};
  if (!forAudience(p, admin)) {
    return (
      <>
        <PageHead crumb={crumb(m.display)} title={p.title} />
        <Empty>This panel is for admins.</Empty>
      </>
    );
  }
  if (!panelSent(m, p, grants)) {
    return (
      <>
        <PageHead crumb={crumb(m.display)} title={p.title} />
        <Empty>Nothing is sent from this workspace, so there is nothing to show.</Empty>
      </>
    );
  }
  const drawn = drawnFor(m, p.elements, admin, ids, grants);
  if (drawn.length === 0) {
    return (
      <>
        <PageHead crumb={crumb(m.display)} title={p.title} />
        <Empty>{p.title} does not run in this workspace, so there is nothing to show.</Empty>
      </>
    );
  }
  const ctx = {
    m,
    ids,
    admin,
    selection,
    select,
    recordTo: (key: string) => tenantPath(s.tenant, "solutions", name, panel, key),
  };
  if (recordKey !== undefined) {
    const list = recordList(drawn);
    const back = (
      <>
        {crumb(m.display)}
        <span>/</span>
        <Link to={tenantPath(s.tenant, "solutions", name, panel)}>{p.title}</Link>
      </>
    );
    if (list === undefined) {
      return (
        <>
          <PageHead crumb={back} title="Not a record" />
          <Empty>{p.title} lists nothing that opens a record.</Empty>
        </>
      );
    }
    return <RecordPage ctx={ctx} list={list} recordKey={recordKey} crumb={back} />;
  }
  const form = openForm(forms.open, drawn);
  if (form !== undefined) {
    return <DeclaredForm ctx={ctx} e={form} back={p.title} onClose={forms.close} />;
  }
  return (
    <>
      <PageHead crumb={crumb(m.display)} title={p.title} line={panelLine(m, p, grants)} />
      <Chips
        p={p}
        selection={selection}
        onRemove={(field) => {
          select(field);
        }}
        onClear={clear}
      />
      <Elements ctx={ctx} elements={drawn} />
    </>
  );
}
